import React, { useState } from 'react';
import {
  useLoaderData,
  useRouter,
} from '@tanstack/react-router';
import pluralize from 'pluralize';

import {
  APIacceptTimelineContributor,
  APIdeclineTimelineContributor,
} from '../../api/Timeline';

import DashboardInvitation from './Invitation';

import type { ContributorInvitation } from '../../types/contributor-invitation';

const DashboardInvitations: React.FC = () => {
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const { invitations: invitationsData } = useLoaderData({ strict: false });
  const invitations = invitationsData?.data || [];
  const totalInvitations = invitationsData?.meta.total;

  const router = useRouter();

  const acceptInvitation = async (invitation: ContributorInvitation) => {
    setError(null);
    setSaving(true);
    try {
      await APIacceptTimelineContributor(invitation.timeline.id, invitation.id);
    } catch (error) {
      console.error('Error accepting invitation:', error);
      setError((error as Error).message || 'An error occurred while accepting the invitation.');
    } finally {
      router.invalidate();
      setSaving(false);
    }
  };

  const declineInvitation = async (invitation: ContributorInvitation) => {
    setError(null);
    setSaving(true);
    try {
      await APIdeclineTimelineContributor(invitation.timeline.id, invitation.id);
    } catch (error) {
      console.error('Error declining invitation:', error);
      setError((error as Error).message || 'An error occurred while declining the invitation.');
    } finally {
      router.invalidate();
      setSaving(false);
    }
  };

  return (
    <>
      <div className="dashboard-header page-header">
        <h2 className="page-title">My <em>pending invitations</em></h2>
        <p>All invitations to contribute to timelines</p>
      </div>
      <div className="dashboard-invitations wa-stack wa-gap-0 inner">
        <div className="wa-grid">
          {(invitations.length > 0) && (
            <div className="page-eyebrow">
              Invitations
            </div>
          )}
          {totalInvitations > 0 && (
            <div className="total-items">
              {totalInvitations} {pluralize('invitation', totalInvitations)}
            </div>
          )}
        </div>
        {invitations.length === 0 && (
          <div className="empty-state wa-stack wa-gap-md">
            <div className="center">
              You haven't received any invitations yet.
            </div>
          </div>
        )}
        {error && (
          <wa-callout variant="danger">
            {error}
          </wa-callout>
        )}
        <div className="dashboard-invitations-list wa-stack">
          {invitations.map((invitation: ContributorInvitation) => (
            <DashboardInvitation
              key={invitation.id}
              invitation={invitation}
              acceptInvitation={acceptInvitation}
              declineInvitation={declineInvitation}
              disabled={saving}
            />
          ))}
        </div>
      </div>
    </>
  );
}

export default DashboardInvitations;
